import type { RootOptions } from 'react-dom/client';

/** The `createRoot` callbacks React reports its own errors through. */
export type RootErrorOptions = Pick<RootOptions, 'onUncaughtError' | 'onCaughtError' | 'onRecoverableError'>;

/** One callback that runs every given handler, so one failing sink never stops the others. Undefined if there are none. */
function fanOut<Args extends unknown[]>(
  handlers: Array<((...args: Args) => void) | undefined>
): ((...args: Args) => void) | undefined {
  const present = handlers.filter((handler): handler is (...args: Args) => void => handler !== undefined);
  if (present.length === 0) return undefined;

  return (...args) => {
    for (const handler of present) {
      try {
        handler(...args);
      } catch {
        // A reporting failure must never break the app, or starve the remaining sinks.
      }
    }
  };
}

/**
 * Combines several sets of `createRoot` error callbacks (e.g. OpenTelemetry's and Sentry's) into one, callback by
 * callback. Unlike composing loggers, this lets each sink keep the handler that suits it: Sentry's `reactErrorHandler`
 * links the component stack and is deliberately not attached to caught errors, which a Sentry `ErrorBoundary` reports.
 */
export function composeRootErrorOptions(...optionSets: RootErrorOptions[]): RootErrorOptions {
  return {
    onUncaughtError: fanOut(optionSets.map((options) => options.onUncaughtError)),
    onCaughtError: fanOut(optionSets.map((options) => options.onCaughtError)),
    onRecoverableError: fanOut(optionSets.map((options) => options.onRecoverableError)),
  };
}
